import { Badge, Button, Group, Stack, Table, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { ConfirmActionModal, PageHeader } from "@how/ui";
import { AudienceModal } from "./AudienceModal";
import { CommunicationsTabs } from "./CommunicationsTabs";
import { useCampuses } from "../campuses/campus-queries";
import { useGroups } from "../groups/group-queries";
import { useTags } from "../people/tag-queries";
import {
  type AudienceFilter,
  type AudienceRow,
  STANDING_LABEL,
  useAudiences,
  useCustomFieldDefinitions,
  useDeleteAudience,
} from "./audience-queries";

interface Named {
  readonly id: string;
  readonly name: string;
}

/** The names behind the ids, from the lists the audience editor reads anyway (cached, so cheap here). */
interface FilterNames {
  readonly tags: readonly Named[];
  readonly campuses: readonly Named[];
  readonly groups: readonly Named[];
  readonly fields: readonly { readonly id: string; readonly label: string }[];
}

const HOUSEHOLD_ROLE_LABEL = { adult: "Adults", child: "Children" } as const;

function names(ids: readonly string[], known: readonly Named[]): string {
  const found = ids.flatMap((id) => known.find((k) => k.id === id)?.name ?? []);
  // Not loaded yet, or a tag, campus or group since deleted: say how many rather than show an id.
  return found.length === ids.length ? found.join(", ") : `${String(ids.length)} chosen`;
}

/** "Tag: Volunteers", "Campus: Downtown": what a filter picks, in the words the church gave it. */
export function audienceFilterLabel(filter: AudienceFilter, known: FilterNames): string {
  switch (filter.type) {
    case "tag":
      return `Tag: ${names(filter.tag_ids, known.tags)}`;
    case "campus":
      return `Campus: ${names(filter.campus_ids, known.campuses)}`;
    case "group":
      return `Group: ${names(filter.group_ids, known.groups)}`;
    case "standing":
      return `Standing: ${filter.values.map((v) => STANDING_LABEL[v]).join(", ")}`;
    case "household_role":
      return `Household: ${filter.values.map((v) => HOUSEHOLD_ROLE_LABEL[v]).join(", ")}`;
    case "custom_field":
      return `Field: ${known.fields.find((f) => f.id === filter.definition_id)?.label ?? "custom field"}`;
  }
}

/**
 * Communications, audiences first: the saved definitions a campaign will send to. A definition is re-resolved every
 * time it is used, so the list shows what it filters on, and the editor shows who it reaches today.
 */
export function AudiencesPage() {
  const canCompose = usePermission("communications.compose", { write: true });
  const audiences = useAudiences();
  const tags = useTags();
  const campuses = useCampuses();
  const groups = useGroups({ search: "", status: "active" });
  const fields = useCustomFieldDefinitions();
  const known: FilterNames = {
    tags: tags.data ?? [],
    campuses: campuses.data ?? [],
    groups: groups.data ?? [],
    fields: fields.data ?? [],
  };
  const remove = useDeleteAudience();
  const [editing, setEditing] = useState<AudienceRow | null | "new">(null);
  const [deleting, setDeleting] = useState<AudienceRow | null>(null);

  return (
    <>
      <PageHeader
        title="Communications"
        description="Who the church can reach, what it says, and what it has sent."
        actions={
          canCompose.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEditing("new");
              }}
            >
              New audience
            </Button>
          ) : null
        }
      />
      <CommunicationsTabs active="audiences" />
      <Stack gap="md">
        <Text size="sm" c="dimmed" maw={640}>
          A saved set of filters, worked out again every time it is used. Open one to see how many
          it reaches by text or by email today, and how many have not agreed yet.
        </Text>
        {(audiences.data ?? []).length === 0 ? (
          <Text size="sm" c="dimmed">
            No audiences yet.
          </Text>
        ) : (
          <div className="how-table-frame" style={{ overflowX: "auto" }}>
            <Table
              verticalSpacing={9}
              className="how-table"
              highlightOnHover
              aria-label="Audiences"
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Filters</Table.Th>
                  <Table.Th className="how-numeric">Updated</Table.Th>
                  <Table.Th aria-label="Actions" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(audiences.data ?? []).map((row) => (
                  <Table.Tr
                    key={row.id}
                    style={{ cursor: "pointer" }}
                    tabIndex={0}
                    onClick={() => {
                      setEditing(row);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") setEditing(row);
                    }}
                  >
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {row.name}
                      </Text>
                      {row.description === null ? null : (
                        <Text size="xs" c="dimmed">
                          {row.description}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {row.definition.filters.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          Everyone
                        </Text>
                      ) : (
                        <Group gap={4}>
                          {row.definition.filters.map((f, i) => (
                            <Badge key={i} variant="light" color="gray">
                              {audienceFilterLabel(f, known)}
                            </Badge>
                          ))}
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td className="how-numeric">
                      {new Date(row.updatedAt).toLocaleDateString()}
                    </Table.Td>
                    <Table.Td>
                      {canCompose.data === true ? (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="red"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleting(row);
                          }}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Stack>

      {editing === null ? null : (
        <AudienceModal
          key={editing === "new" ? "new" : editing.id}
          opened
          audience={editing === "new" ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      <ConfirmActionModal
        opened={deleting !== null}
        title="Delete audience"
        message={`"${deleting?.name ?? ""}" will no longer be offered to campaigns. Nothing about the people in it changes.`}
        confirmLabel="Delete"
        danger
        loading={remove.isPending}
        onClose={() => {
          setDeleting(null);
        }}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate(deleting.id, {
              onSuccess: () => {
                setDeleting(null);
              },
            });
          }
        }}
      />
    </>
  );
}
